import { useFormikContext } from 'formik'
import { Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useStyles } from 'src/components/un-sdg/un-sdg-list/styles'
import { ETranslationNs, ITranslationForm } from 'src/types'
import { useUpdateAITranslation } from 'src/hooks'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { ETranslationType } from 'src/types/generate-localization.type'

interface IProps {
  translationId: string
  isDefaultLng: boolean
}

const UpdateTranslationFormInner = ({ translationId, isDefaultLng }: IProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Form])
  const { mutateAsync: updateAIValue, isLoading: isRegenerating } =
    useUpdateAITranslation(translationId)
  const { errors, values, handleChange, isSubmitting, dirty, setFieldValue } =
    useFormikContext<ITranslationForm>()
  const classes = useStyles()

  const hasErrors = Object.keys(errors).length > 0

  const handlePopulate = (type: ETranslationType) => {
    if (type === ETranslationType.TITLE) {
      setFieldValue('value', values.ai_translation)
    } else {
      setFieldValue('helper_text', values.ai_translation_helper)
    }
  }

  const handleRegenerate = async (type: ETranslationType) => {
    const newValue = await updateAIValue(type)
    if (!newValue) return
    setFieldValue(
      type === ETranslationType.HELPER ? 'ai_translation_helper' : 'ai_translation',
      newValue,
    )
  }
  return (
    <div className={classes.formWrapper}>
      <div className={classes.inputsWrapper}>
        <Typography className={classes.title}>
          <HelperWrapper t="translations-form-title" />
        </Typography>
        <div className={classes.inputsWrapper}>
          <>
            {/* TEXT FIELD WITH NAMESPACE */}
            <TextField
              variant="standard"
              type="text"
              label={t('name-input-label.v', { ns: ETranslationNs.Form })}
              value={values.namespace}
              disabled
            />
            {/* REGUALR NAME ORIGINAL VALUE HOLDER */}
            {!isDefaultLng && (
              <TextField
                multiline
                disabled
                variant="standard"
                type="text"
                label={t('original-translation-input-label.v')}
                value={values.default_translation}
              />
            )}
            {/* TEXT FIELD WITH ORIGINAL TRANSLATION */}
            <TextField
              multiline
              autoComplete="off"
              variant="standard"
              id="value"
              name="value"
              onChange={handleChange}
              type="text"
              label={t('translation-input-label.v') + '*'}
              value={values.value}
              helperText={
                <Typography className={classes.warningText}>
                  {errors.value ? <HelperWrapper t={errors.value} /> : ''}
                </Typography>
              }
            />
            {/* REGULAR TEXT AI GENERATOR */}
            {!isDefaultLng && (
              <div>
                <TextField
                  multiline
                  fullWidth
                  variant="standard"
                  label={t('ai-translation-input-label.v')}
                  value={values.ai_translation}
                  disabled
                />
                <div>
                  <Button onClick={() => handlePopulate(ETranslationType.TITLE)}>
                    <HelperWrapper t="translations-form-ai-populate" />
                  </Button>
                  <Button
                    disabled={isRegenerating}
                    onClick={() => handleRegenerate(ETranslationType.TITLE)}
                  >
                    <HelperWrapper t="translations-form-ai-regenerate" />
                  </Button>
                </div>
              </div>
            )}

            {/* HELPER TEXT ORIGINAL VALUE HOLDER */}
            {!isDefaultLng && (
              <TextField
                multiline
                disabled
                variant="standard"
                type="text"
                label={
                  <HelperWrapper t="helper-text-default-input-label" ns={ETranslationNs.Common} />
                }
                value={values.default_translation_helper}
              />
            )}

            {/* HELPER TEXT ORIGINAL */}
            <TextField
              multiline
              autoComplete="off"
              variant="standard"
              id="helper_text"
              name="helper_text"
              onChange={handleChange}
              type="text"
              label={t('helper-text-input-label.v')}
              value={values.helper_text}
              // helperText={
              //   <Typography className={classes.warningText}>
              //     {errors.value ? <HelperWrapper t={errors.value}/> : ''}
              //   </Typography>
              // }
            />

            {/* GENERATE + PASTE + TEXT FIELD FOR HELPER TEXT */}
            {!isDefaultLng && (
              <div>
                <TextField
                  multiline
                  fullWidth
                  variant="standard"
                  label={t('ai-translation-input-label.v')}
                  value={values.ai_translation_helper}
                  disabled
                />
                <div>
                  <Button onClick={() => handlePopulate(ETranslationType.HELPER)}>
                    <HelperWrapper t="translations-form-ai-populate" />
                  </Button>
                  <Button
                    disabled={isRegenerating}
                    onClick={() => handleRegenerate(ETranslationType.HELPER)}
                  >
                    <HelperWrapper t="translations-form-ai-regenerate" />
                  </Button>
                </div>
              </div>
            )}

            <div>
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleChange}
                    name="review_required"
                    checked={values.review_required}
                  />
                }
                label={
                  <Typography>
                    <HelperWrapper
                      t="translations-table-review-column"
                      ns={ETranslationNs.Common}
                    />
                  </Typography>
                }
              />
            </div>
          </>
        </div>
      </div>
      <Button variant="contained" disabled={isSubmitting || !dirty || hasErrors} type="submit">
        <HelperWrapper t="translations-form-submit" />
      </Button>
    </div>
  )
}

export { UpdateTranslationFormInner }
